import { 
  Box, 
  Stack, 
  Group, 
  Text, 
  Card,
  Title, 
  Button, 
  ActionIcon, 
  Loader, 
  Modal, 
  TextInput, 
  Textarea, 
  SimpleGrid,
  AspectRatio,
  UnstyledButton,
  Image,
  Badge,
  Center,
  ScrollArea,
  VisuallyHidden,
  Alert,
} from '@mantine/core';
import {
  IconArrowDown,
  IconCameraPlus,
  IconFlag,
  IconHeart,
  IconBookmark,
  IconHash,
  IconMapPin,
  IconMapPinFilled,
  IconPhotoHeart,
  IconPhotoVideo,
  IconRefresh,
  IconX,
  IconAlertCircle,
} from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';
import { ARTICLE_MAX_IMAGES, ARTICLE_MAX_VIDEOS } from '@shared/media/uploadPolicy';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ImageSourcePicker } from '@shared/ui/ImageSourcePicker';
import type { Article, ArticleLocation, ArticleVideo } from '@domain/plaza/types';
import { resolveMediaUrl } from '@domain/media/resolve';
import { POST_CONTENT_MAX } from '@shared/config/runtime';
import { UserAvatar } from '@shared/ui/UserAvatar';
import { formatListTime, initials } from '@shared/utils/ui';
import type { PlazaRefreshSource } from '../hooks/usePlazaFeed';
import { submitReport } from '@domain/safety/report';
import { WorkspaceEmptyState } from '../components/WorkspaceEmptyState';

const TOUCH_PULL_TO_REFRESH_QUERY = '(hover: none) and (pointer: coarse)';

type PostComposerImage = {
  id: string;
  file: File;
  previewUrl: string;
};

type PostComposerVideo = {
  id: string;
  file: File;
  previewUrl: string;
};

type PlazaStageProps = {
  apiBase: string;
  cdnBase: string;
  plazaArticles: Article[];
  plazaHasMore: boolean;
  plazaLoadState: 'idle' | 'loading' | 'loadingMore' | 'error';
  plazaError: string | null;
  setIsPlazaAtTop: React.Dispatch<React.SetStateAction<boolean>>;
  plazaRefreshSource: PlazaRefreshSource;
  isPostModalOpen: boolean;
  setIsPostModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  postTitle: string;
  setPostTitle: React.Dispatch<React.SetStateAction<string>>;
  postContent: string;
  setPostContent: React.Dispatch<React.SetStateAction<string>>;
  postTags: string;
  setPostTags: React.Dispatch<React.SetStateAction<string>>;
  postSubmitting: boolean;
  postSubmitStatus: string | null;
  postSubmitError: string | null;
  postImages: PostComposerImage[];
  postVideo: PostComposerVideo | null;
  postLocation: ArticleLocation | null;
  postLocationPending: boolean;
  canSubmitPost: boolean;
  closePostComposer: () => void;
  loadPlazaArticles: (refresh?: boolean, source?: PlazaRefreshSource) => Promise<void>;
  handleSubmitPost: () => Promise<void>;
  handleAddPostImages: (files: File[]) => void;
  handleRemovePostImage: (imageId: string) => void;
  handleSelectPostVideo: (files: File[]) => void;
  handleRemovePostVideo: () => void;
  handleUseCurrentLocation: () => void;
  removePostLocation: () => void;
  handleToggleArticleLike: (article: Article) => Promise<void>;
  handleToggleArticleFavorite: (article: Article) => Promise<void>;
  setPreviewImageUrl: React.Dispatch<React.SetStateAction<string | null>>;
  onViewAuthorProfile: (authorUserId: number) => void;
  viewSwitcher: React.ReactNode;
};

function formatArticlePreview(text: string) {
  return text
    .split('\n')
    .map((line) => line.trim().replace(/^#{1,6}\s+/, ''))
    .filter(Boolean)
    .join(' ')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1');
}

export function PlazaStage({
  apiBase,
  cdnBase,
  plazaArticles,
  plazaHasMore,
  plazaLoadState,
  plazaError,
  setIsPlazaAtTop,
  plazaRefreshSource,
  isPostModalOpen,
  setIsPostModalOpen,
  postTitle,
  setPostTitle,
  postContent,
  setPostContent,
  postTags,
  setPostTags,
  postSubmitting,
  postSubmitStatus,
  postSubmitError,
  postImages,
  postVideo,
  postLocation,
  postLocationPending,
  canSubmitPost,
  closePostComposer,
  loadPlazaArticles,
  handleSubmitPost,
  handleAddPostImages,
  handleRemovePostImage,
  handleSelectPostVideo,
  handleRemovePostVideo,
  handleUseCurrentLocation,
  removePostLocation,
  handleToggleArticleLike,
  handleToggleArticleFavorite,
  setPreviewImageUrl,
  onViewAuthorProfile,
  viewSwitcher,
}: PlazaStageProps) {
  const isRefreshing = plazaLoadState === 'loading' && plazaArticles.length > 0;
  const hasPostTextOrMedia = Boolean(postContent.trim() || postImages.length > 0 || postVideo);
  const canAddPostMedia = postImages.length < ARTICLE_MAX_IMAGES || !postVideo;
  const enablePullToRefresh = useMediaQuery(
    TOUCH_PULL_TO_REFRESH_QUERY,
    false,
    { getInitialValueInEffect: false },
  );
  const isMobile = useMediaQuery('(max-width: 47.99em)', false, { getInitialValueInEffect: false });

  return (
    <>
      <Box style={{ height: '100%', width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <Box
          className="plaza-toolbar hubs-stage__toolbar"
          data-testid="plaza-toolbar"
          px={0}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            height: 50,
            flexShrink: 0,
            background: 'transparent',
            marginBottom: 8,
          }}
        >
          {viewSwitcher}
          <Group gap={8} wrap="nowrap">
            <Button
              className="plaza-toolbar__compose"
              data-testid="plaza-compose-trigger"
              size="xs"
              radius="xl"
              leftSection={<IconCameraPlus size={15} />}
              onClick={() => setIsPostModalOpen(true)}
            >
              发布动态
            </Button>
          </Group>
        </Box>

        <Box

          id="plaza-feed-scroll"
          style={{ 
            flex: 1, 
            overflowY: 'auto',
            padding: '0 16px 28px',
            background: 'var(--locs-canvas)',
            position: 'relative'
          }}
        >
          {plazaLoadState === 'loading' && plazaArticles.length === 0 ? (
            <Center
              data-testid="plaza-initial-loading"
              style={{ position: 'absolute', inset: 0 }}
            >
              <Stack align="center" gap="sm">
                <Loader size="lg" />
                <Text size="sm" c="dimmed">正在加载动态</Text>
              </Stack>
            </Center>
          ) : (
            <>
              {isRefreshing && (
                <Center
                  style={{
                    position: 'absolute',
                    top: 10,
                    left: 0,
                    right: 0,
                    zIndex: 5,
                    pointerEvents: 'none',
                  }}
                >
                  <Group
                    gap={6}
                    px="sm"
                    py={6}
                    style={{
                      borderRadius: 999,
                      backgroundColor: 'rgba(255, 253, 248, 0.94)',
                      border: '1px solid var(--locs-border)',
                      boxShadow: '0 10px 24px rgba(27, 18, 11, 0.12)',
                      backdropFilter: 'blur(12px)',
                    }}
                  >
                    <Loader size={14} color="terracotta" />
                    <Text size="xs" fw={700} c="terracotta.7">
                      {plazaRefreshSource === 'pull' ? '正在更新动态' : '正在同步最新动态'}
                    </Text>
                  </Group>
                </Center>
              )}
              {!plazaError && plazaArticles.length === 0 && (
                <Center
                  data-testid="plaza-empty-state"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 1,
                    pointerEvents: 'none',
                    transform: 'translateY(-4vh)',
                  }}
                >
                  <WorkspaceEmptyState
                    icon={<IconPhotoHeart size={30} stroke={2} />}
                    message="还没有动态"
                  />
                </Center>
              )}
              <InfiniteScroll
                dataLength={plazaArticles.length}
                next={() => void loadPlazaArticles(false)}
                hasMore={plazaHasMore}
                onScroll={(event) => {
                  const target = event.target as HTMLElement | null;
                  if (!target) {
                    return;
                  }
                  setIsPlazaAtTop(target.scrollTop <= 6);
                }}
                loader={(
                  <Center py="xl">
                    <Loader size="sm" />
                  </Center>
                )}
                endMessage={
                  plazaArticles.length > 0 && plazaLoadState === 'idle'
                    ? (
                      <Text ta="center" size="xs" c="dimmed" py="lg" fw={700} lts="0.08em">
                        没有更多内容了
                      </Text>
                    )
                    : undefined
                }
                refreshFunction={() => loadPlazaArticles(true, 'pull')}
                pullDownToRefresh={enablePullToRefresh}
                pullDownToRefreshThreshold={70}
                pullDownToRefreshContent={
                  <Center py="sm">
                    <Group gap={6}>
                      <IconArrowDown size={16} color="var(--mantine-color-terracotta-7)" />
                      <Text size="xs" fw={700} c="terracotta.7">下拉更新动态</Text>
                    </Group>
                  </Center>
                }
                releaseToRefreshContent={
                  <Center py="sm">
                    <Group gap={6}>
                      <IconRefresh size={16} color="var(--mantine-color-terracotta-7)" />
                      <Text size="xs" fw={700} c="terracotta.7">松开立即更新</Text>
                    </Group>
                  </Center>
                }
                scrollableTarget="plaza-feed-scroll"
                style={{ maxWidth: 760, margin: '0 auto', width: '100%' }}
              >
                <Box h={20} aria-hidden="true" />
                {plazaError ? (
                  <Center py={40}>
                    <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--mantine-spacing-md)' }}>
                      <Text c="red" size="sm">{plazaError}</Text>
                      <Button variant="light" color="terracotta" onClick={() => void loadPlazaArticles(true, 'button')}>
                        重试
                      </Button>
                    </Box>
                  </Center>
                ) : plazaArticles.length > 0 ? (
                  <Stack gap="lg">
                    {plazaArticles.map((article, index) => {
                      const images = article.media?.images ?? [];
                      const video = article.media?.video ?? null;
                      const coverCount = Math.min(images.length, ARTICLE_MAX_IMAGES);
                      const authorName = article.author?.displayName || `用户 #${article.authorUserId}`;
                      const authorAvatarUrl = resolveMediaUrl(article.author?.avatar, cdnBase);
                      return (
                        <Card 
                          key={`${article.id}-${index}`} 
                          component="article"
                          p={0}
                          radius="lg"
                          withBorder
                          shadow="sm"
                          style={{
                            backgroundColor: 'var(--locs-panel)',
                            transition: 'all 0.2s ease',
                            overflow: 'hidden',
                          }}
                        >
                          <Group p="md" pb="sm" wrap="nowrap" align="flex-start">
                            <Group
                              gap="sm"
                              onClick={() => onViewAuthorProfile(article.authorUserId)}
                              style={{ cursor: 'pointer' }}
                            >
                              <UserAvatar 
                                id={article.authorUserId}
                                name={authorName}
                                avatarUrl={authorAvatarUrl}
                                size={38}
                                radius="md"
                                fallback={initials(authorName)}
                              />
                              <Stack gap={0}>
                                <Text size="sm" fw={800}>{authorName}</Text>
                                <Text size="xs" c="dimmed">{formatListTime(article.created)}</Text>
                              </Stack>
                            </Group>
                          </Group>

                          {(article.title || article.body) && (
                            <Box px="md" pb="md">
                              {article.title && (
                                <Title order={3} size="h4" mb={article.body ? 4 : 0} fw={850}>
                                  {article.title}
                                </Title>
                              )}
                              {article.body && (
                                <Text size="sm" lineClamp={3} c="dimmed" lh={1.65}>
                                  {formatArticlePreview(article.body)}
                                </Text>
                              )}
                              {article.tags.length > 0 && (
                                <Group gap={4} mt="xs">
                                  {article.tags.map((tag) => (
                                    <Badge key={tag} variant="outline" color="gray" radius="xs" size="xs">#{tag}</Badge>
                                  ))}
                                </Group>
                              )}
                            </Box>
                          )}

                          {video && (
                            <Box>
                              <video
                                controls
                                playsInline
                                preload="metadata"
                                src={resolveMediaUrl({ provider: video.provider, url: video.url }, cdnBase)}
                                style={{ width: '100%', display: 'block', background: '#111' }}
                              />
                            </Box>
                          )}

                          {images.length > 0 && (
                            <SimpleGrid 
                              cols={coverCount === 1 ? 1 : coverCount <= 4 ? 2 : { base: 2, sm: 3 }}
                              spacing={2}
                            >
                              {images.slice(0, ARTICLE_MAX_IMAGES).map((payload, imageIndex) => {
                                const imageUrl = resolveMediaUrl({ provider: payload.provider, url: payload.url }, cdnBase);
                                return (
                                  <AspectRatio 
                                    ratio={coverCount === 1 ? 16 / 9 : 1} 
                                    key={`${article.id}-${imageIndex}-${payload.mediaId}`}
                                  >
                                    <UnstyledButton
                                      onClick={() => setPreviewImageUrl(imageUrl)}
                                      style={{
                                        overflow: 'hidden',
                                        borderRadius: 0,
                                        position: 'relative'
                                      }}
                                    >
                                      <Image 
                                        src={imageUrl} 
                                        alt={article.title || `动态图片 ${imageIndex + 1}`}
                                        fit="cover"
                                        height="100%"
                                        style={{
                                          transition: 'transform 0.3s ease',
                                        }}
                                      />
                                    </UnstyledButton>
                                  </AspectRatio>
                                );
                              })}
                            </SimpleGrid>
                          )}
                          <Group justify="space-between" px="sm" py={6}>
                            <Group gap={6}>
                              <Button
                                variant={article.viewer.liked ? 'light' : 'subtle'}
                                color="red"
                                size="compact-xs"
                                leftSection={<IconHeart size={14} fill={article.viewer.liked ? 'currentColor' : 'none'} />}
                                onClick={() => void handleToggleArticleLike(article)}
                              >
                                {article.stats.likeCount}
                              </Button>
                              <Button
                                variant={article.viewer.favorited ? 'light' : 'subtle'}
                                color="yellow"
                                size="compact-xs"
                                leftSection={<IconBookmark size={14} fill={article.viewer.favorited ? 'currentColor' : 'none'} />}
                                onClick={() => void handleToggleArticleFavorite(article)}
                              >
                                {article.stats.favoriteCount}
                              </Button>
                            </Group>
                            <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="sm"
                            radius="md"
                            title="举报该内容"
                            aria-label="举报该内容"
                            onClick={() => {
                              const detail = window.prompt('请补充该内容的举报说明（可选）') || undefined;
                              void submitReport('article', String(article.id), 'other', detail, apiBase)
                                .then(() => window.alert('内容举报已提交'))
                                .catch((error) => window.alert(`举报失败：${error.message}`));
                            }}
                          >
                              <IconFlag size={14} />
                            </ActionIcon>
                          </Group>
                        </Card>
                      );
                    })}
                  </Stack>
                ) : null}
              </InfiniteScroll>
            </>
          )}
        </Box>

        {/* Mobile FAB: compose post — only visible on mobile */}
        <ActionIcon
          hiddenFrom="sm"
          className="plaza-fab"
          aria-label="发布动态"
          title="发布动态"
          onClick={() => setIsPostModalOpen(true)}
          size={56}
          radius="xl"
          variant="filled"
          color="terracotta"
        >
          <IconCameraPlus size={24} stroke={2} />
        </ActionIcon>
      </Box>

      <Modal
        opened={isPostModalOpen}
        onClose={closePostComposer}
        title={<VisuallyHidden>发布动态</VisuallyHidden>}
        fullScreen={isMobile}
        size="lg"
        radius={isMobile ? 0 : 'lg'}
        padding={0}
        withCloseButton
        closeButtonProps={{ 'aria-label': '关闭发布动态' }}
        centered={!isMobile}
        overlayProps={isMobile ? undefined : {
          backgroundOpacity: 0.55,
          blur: 3,
        }}
        styles={{
          content: {
            overflow: 'hidden',
            ...(isMobile ? {
              paddingTop: 'env(safe-area-inset-top, 0px)',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              display: 'flex',
              flexDirection: 'column',
            } : {
              maxHeight: 'calc(100dvh - 24px)',
            }),
          },
          header: {
            minHeight: 42,
            padding: isMobile ? '12px 18px 0' : '12px 18px 0',
          },
          body: {
            background: 'var(--locs-panel)',
            overflow: 'hidden',
            ...(isMobile ? { flex: 1, minHeight: 0 } : {}),
          },
        }}
      >
        <Box className="post-composer-layout">
          <ScrollArea
            className="post-composer-scroll"
            type="auto"
            scrollbars="y"
            scrollbarSize={4}
            offsetScrollbars="y"
            scrollHideDelay={600}
          >
            <Stack
              gap="md"
              px={{ base: 'md', sm: 'lg' }}
              pt={{ base: 'md', sm: 'lg' }}
              pb="md"
              maw={820}
              mx="auto"
            >
            <TextInput
              variant="unstyled"
              placeholder="起个标题"
              value={postTitle}
              onChange={(e) => setPostTitle(e.target.value)}
              maxLength={80}
              aria-label="标题"
              classNames={{ input: 'post-composer-field post-composer-title' }}
              styles={{ input: { backgroundColor: 'transparent', borderColor: 'transparent', borderRadius: 0 } }}
            />
            <Textarea
              variant="unstyled"
              placeholder="写下你想分享的内容"
              value={postContent}
              onChange={(e) => setPostContent(e.target.value)}
              maxLength={POST_CONTENT_MAX}
              minRows={7}
              autosize
              aria-label="正文"
              classNames={{ input: 'post-composer-field post-composer-body' }}
              styles={{ input: { backgroundColor: 'transparent', borderColor: 'transparent', borderRadius: 0 } }}
            />
            <Group
              className="post-composer-meta-row"
              gap="xs"
              wrap="nowrap"
              aria-label="动态附加信息"
            >
              <ActionIcon
                className="post-composer-attachment-trigger"
                variant="subtle"
                color="terracotta"
                size="lg"
                radius="md"
                loading={postLocationPending}
                disabled={postSubmitting}
                onClick={postLocation ? removePostLocation : handleUseCurrentLocation}
                aria-label={postLocation ? '移除位置' : '添加位置'}
                aria-pressed={Boolean(postLocation)}
                title={postLocation ? `${postLocation.label}，点击移除位置` : '添加模糊位置'}
              >
                {postLocation
                  ? <IconMapPinFilled size={19} />
                  : <IconMapPin size={20} stroke={1.8} />}
              </ActionIcon>

              <TextInput
                variant="unstyled"
                placeholder="输入标签，空格分隔"
                value={postTags}
                onChange={(e) => setPostTags(e.target.value)}
                maxLength={160}
                leftSection={<IconHash size={16} />}
                aria-label="标签"
                classNames={{ input: 'post-composer-field post-composer-tags' }}
                className="post-composer-tags-control"
                styles={{
                  root: { flex: 1, minWidth: 0 },
                  input: { backgroundColor: 'transparent', borderColor: 'transparent', borderRadius: 0 },
                }}
              />
            </Group>

            {(postImages.length > 0 || postVideo || canAddPostMedia) && (
              <SimpleGrid cols={{ base: 3, xs: 4, sm: 5 }} spacing={8}>
                {postImages.map((image) => (
                  <AspectRatio ratio={1} key={image.id}>
                    <Box
                      style={{
                        position: 'relative',
                        borderRadius: 'var(--mantine-radius-md)',
                        overflow: 'hidden',
                        border: '1px solid var(--locs-border)',
                        background: 'var(--locs-canvas)',
                      }}
                    >
                      <Image src={image.previewUrl} alt={image.file.name} fit="cover" height="100%" />
                      <ActionIcon
                        variant="filled"
                        color="dark"
                        size="sm"
                        radius="md"
                        onClick={() => handleRemovePostImage(image.id)}
                        style={{ position: 'absolute', top: 5, right: 5, zIndex: 5, opacity: 0.86 }}
                        aria-label="移除图片"
                      >
                        <IconX size={12} stroke={2.5} />
                      </ActionIcon>
                    </Box>
                  </AspectRatio>
                ))}

                {postVideo && (
                  <AspectRatio ratio={1}>
                    <Box
                      style={{
                        position: 'relative',
                        borderRadius: 'var(--mantine-radius-md)',
                        overflow: 'hidden',
                        background: 'var(--locs-espresso)',
                      }}
                    >
                      <video
                        src={postVideo.previewUrl}
                        controls
                        playsInline
                        style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
                      />
                      <ActionIcon
                        variant="filled"
                        color="dark"
                        size="sm"
                        radius="md"
                        onClick={handleRemovePostVideo}
                        style={{ position: 'absolute', top: 5, right: 5, zIndex: 5, opacity: 0.86 }}
                        aria-label="移除视频"
                      >
                        <IconX size={12} stroke={2.5} />
                      </ActionIcon>
                    </Box>
                  </AspectRatio>
                )}

                {canAddPostMedia && (
                  <ImageSourcePicker
                    multiple
                    allowImages={postImages.length < ARTICLE_MAX_IMAGES}
                    disabled={postSubmitting}
                    onSelect={handleAddPostImages}
                    onSelectVideo={postVideo || ARTICLE_MAX_VIDEOS < 1 ? undefined : handleSelectPostVideo}
                    renderTrigger={({ open, openState }) => (
                      <UnstyledButton
                        className="post-composer-media-picker"
                        data-open={openState || undefined}
                        onClick={open}
                        disabled={postSubmitting}
                        aria-label="添加媒体"
                        title="添加图片或视频"
                      >
                        <span className="post-composer-media-picker__content" aria-hidden="true">
                          <IconPhotoVideo size={24} stroke={1.7} />
                        </span>
                      </UnstyledButton>
                    )}
                  />
                )}
              </SimpleGrid>
            )}
            </Stack>
          </ScrollArea>

          <Group className="post-composer-actions" justify="flex-end">
            <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
              {postSubmitError && (
                <Alert color="red" icon={<IconAlertCircle size={16} />} py="xs">
                  {postSubmitError}
                </Alert>
              )}
              {postSubmitStatus && <Text size="sm" c="dimmed" role="status">{postSubmitStatus}</Text>}
            </Stack>
            <Group gap="xs">
                <Button
                  loading={postSubmitting}
                  onClick={() => void handleSubmitPost()}
                  disabled={!canSubmitPost}
                  radius="md"
                  size="sm"
                  px="lg"
                >
                  发布
                </Button>
            </Group>
          </Group>
        </Box>
      </Modal>
    </>
  );
}
